/**
 * Shared `queryOptions` for TanStack Query.
 *
 * As always, you can add a query-options.ts file to your component's directory,
 * but if your query is used by more than one component put it in this file.
 *
 * See https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 *
 * Usage Notes
 * ============
 *
 * - Name the options function after the request, like `caseGet` > `caseGetOptions`.
 * - Also name the query key after the request, like `caseGet` > `["caseGet", ...]`.
 * - Set a long `staleTime` for requests that we don't update often to avoid unnecessary re-fetches.
 *   E.g. the `getFirm` request could have `staleTime: Infinity`. If we update the firm, we can
 *   refetch everywhere the firm data is used with `queryClient.invalidateQueries(firmGetOptions().queryKey)`.
 * - Don't be shy about duplicate requests in components! As long as you're fetching within the
 *   `staleTime` window, react-query will deduplicate the requests. You do not have to pass data down
 *   to multiple layers of child components, just get the data where you need it.
 *
 *
 * Example
 * ============
 *
 * #### src/lib/query-options.ts
 * ```tsx
 * import { useFirmApi } from "@/hooks/useApi";
 *
 * const firmApi = useFirmApi();
 *
 * function firmGetOptions(firmId: string) {
 *   return {
 *     queryKey: ["firm", firmId],
 *     queryFn: () => firmApi.firmGet({ id: firmId }),
 *   };
 * }
 * ```
 *
 * #### src/components/MyComponent/MyComponent.tsx
 * ```tsx
 * const firmGetQuery = useQuery(firmGetOptions("123"));
 * ```
 *
 */

import { queryOptions } from "@tanstack/react-query";
import type {
  CaseFileApi,
  CaseFileType,
  DocumentApi,
  DocumentStatus,
  ProductionSetApi,
} from "@/briefpoint-client";

// AKA "Production Files"
export const getCaseFilesOptions = ({
  caseFileApi,
  caseId,
  fileType,
  status,
  pageSize,
  page,
}: {
  caseFileApi: CaseFileApi;
  caseId: string;
  fileType: CaseFileType;
  status?: DocumentStatus;
  pageSize?: number;
  page?: number;
}) =>
  queryOptions({
    // queryKey is filtered to remove undefined values, so if you want queryKey
    // matching to work, make sure you don't leave off anything in between.
    queryKey: [
      "caseFileApi.caseFileGetCaseFiles",
      caseId,
      fileType,
      status,
      pageSize,
      page,
    ].filter(Boolean),
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileGetCaseFiles({
        caseId: caseId,
        fileType: fileType,
        status,
        pageSize: pageSize,
        page: page,
      });
    },
  });

export const getCaseFilesWithheldListOptions = ({
  caseFileApi,
  caseId,
}: {
  caseFileApi: CaseFileApi;
  caseId: string;
}) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetWithheldList", caseId].filter(Boolean),
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileGetWithheldList({
        caseId: caseId,
      });
    },
  });

export const getCaseFileLatestBatchOptions = (
  caseFileApi: CaseFileApi,
  caseId?: string,
) =>
  queryOptions({
    // Note this endpoint is polled in CaseProduction, so we need to add a unique identifier if this is reused.
    queryKey: ["caseFileApi.caseFileGetCaseFileLatestBatch", caseId],
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");

      return caseFileApi.caseFileGetCaseFileLatestBatch({
        caseId,
      });
    },
  });

export const getPurgatorySummaryOptions = (
  caseFileApi: CaseFileApi,
  caseId: string,
) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetPurgatorySummary", caseId],
    queryFn: () => caseFileApi.caseFileGetPurgatorySummary({ caseId }),
  });

export const getLatestQueryForInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetLatestQueryForInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetLatestQueryForInterrogatory({
        documentId,
        interrogatoryId,
      }),
  });

export const getProductionSetOptions = (
  documentId: string,
  productionSetApi: ProductionSetApi,
  useDocHandling = false,
) =>
  queryOptions({
    queryKey: ["productionSetApi.productionSetGetForDocument", documentId],
    queryFn: () => productionSetApi.productionSetGetForDocument({ documentId }),
    enabled: useDocHandling,
  });

export const getReadUrlOptions = (
  fileId: string,
  caseId: string,
  caseFileApi: CaseFileApi,
) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetReadUrl", fileId, caseId],
    enabled: !!fileId,
    queryFn: async () => {
      const data = await caseFileApi.caseFileGetReadUrl({
        fileId,
        caseId,
      });
      return JSON.parse(data);
    },
  });

export const getResponsiveFilesForInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
  filterWithheld = false,
  useDocHandling = false,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetResponsiveFilesForInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetResponsiveFilesForInterrogatory({
        documentId,
        interrogatoryId,
      }),
    select: (data) => {
      if (filterWithheld) {
        return data.filter((file) => !file.isWithheld);
      }
      return data;
    },
    enabled: useDocHandling,
  });

export const responsiveFileMapOptions = (
  documentId: string,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetResponsiveFileMapForCaseInterrogatories",
      documentId,
    ],
    queryFn: () =>
      documentApi.documentGetResponsiveFileMapForCaseInterrogatories({
        documentId,
      }),
    enabled: !!documentId,
  });

export const searchCaseFilesQueryOptions = (
  caseFileApi: CaseFileApi,
  {
    caseId,
    folderId,
    search,
  }: {
    caseId?: string;
    folderId?: string;
    search?: string;
  },
) =>
  queryOptions({
    queryKey: [
      "caseFileApi.caseFileSearchCaseFiles",
      caseId,
      ...(folderId !== undefined ? [folderId] : []),
      ...(search !== undefined ? [search] : []),
    ],
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileSearchCaseFiles({ caseId, folderId, search });
    },
  });

export const getInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetInterrogatory({ documentId, interrogatoryId }),
    enabled: !!documentId && !!interrogatoryId,
    staleTime: 0,
  });

export const getDocumentOptions = (
  documentId: string,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: ["documentApi.documentGet", documentId],
    queryFn: () => documentApi.documentGet({ documentId }),
  });
